import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed filter state for the list pages (e.g. Sessions). The URL
 * is the single source of truth for filters, which keeps filtered views
 * bookmarkable/shareable, makes browser back/forward work, and lets in-app links
 * that carry query params (e.g. a project link → ?project=…) apply correctly.
 *
 * Returns `searchParams` for reading current values plus two writers:
 * - `setParam` updates a single param, deleting it when set to its default so
 *   URLs stay clean. Uses `replace` so filter tweaks don't spam history.
 * - `clearAllFilters` resets every filter by emptying the query string.
 */
export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = useCallback(
    (key: string, value: string, defaultValue = '') => {
      setSearchParams(
        (prev) => {
          if (!value || value === defaultValue) {
            prev.delete(key);
          } else {
            prev.set(key, value);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return { searchParams, setParam, clearAllFilters };
}
